"use client";

import FilterIcon from "@/app/assets/images/svg/sliders.svg";
import CloseIcon from "@/app/assets/images/svg/xmark.svg";
import Button from "@/components/UI/Button/Button";
import { useI18n } from "@/i18n/client";
import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import { useSearchParams } from "next/navigation";
import { FC, useEffect, useMemo, useState } from "react";
import { useSearchContext } from "@/components/_Search/provider/SearchContextProvider";
import ActiveFilters from "./ActiveFilters/ActiveFilters";
import styles from "./FilterAndSort.module.scss";
import PageSize from "./Sort/PageSize/PageSize";
import SortBy from "./Sort/SortBy/SortBy";
import ToggleFilterAccordion from "./ToggleFilterAccordian/ToggleFilterAccordian";

const FilterAndSort: FC = () => {
	const t = useI18n();

	const searchParams = useSearchParams();

	const { links, sortBy, facets, resetState } = useSearchContext();

	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		setIsOpen(false);
	}, [searchParams]);

	const handleOpen = () => {
		resetState();
		setIsOpen(true);
	};

	const handleClose = () => {
		setIsOpen(false);
	};

	const filterLabel = useMemo(() => {
		const activeSortBy = sortBy.active;

		if (!activeSortBy || activeSortBy.value === sortBy.default.value) {
			return t("Search.filters");
		}

		return activeSortBy.label;
	}, [sortBy.active]);

	return (
		<>
			<Button
				className={styles.button}
				as='button'
				variant='secondary'
				onClick={() => handleOpen()}
			>
				<FilterIcon />
				{filterLabel}
			</Button>
			<Dialog.Root open={isOpen}>
				<Dialog.Portal>
					<Dialog.Overlay
						className={clsx([
							styles.overlay,
							isOpen ? styles.open : styles.closed,
						])}
						onClick={() => handleClose()}
					/>
					<Dialog.Content
						className={clsx([
							styles.content,
							isOpen ? styles.open : styles.closed,
						])}
					>
						<Dialog.Title className={styles.header}>
							{t("Search.sortAndFilter")}
							<Dialog.Close
								className={styles.close}
								asChild
								onClick={() => handleClose()}
							>
								<CloseIcon />
							</Dialog.Close>
						</Dialog.Title>
						<div className={styles.items}>
							<div className={styles.section}>
								<SortBy />
								<PageSize />
							</div>
							<ActiveFilters />
							{facets.options.map((facet) => {
								return (
									<ToggleFilterAccordion key={facet.value} facet={facet} />
								);
							})}
						</div>
						<div className={styles.actions}>
							<Button variant={"primary"} as='link' href={links.apply}>
								{t("Search.showResults")}
							</Button>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		</>
	);
};

export default FilterAndSort;
